import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { BoardWidget, ApiToken } from "../types.js";
import { resultTable, sectionHead, delta, downloadCsv } from "../components.js";
import { formatMetric, fmtDate, timeAgo } from "../format.js";
import { icons } from "../icons.js";
import { resultChart } from "./explore.js";

interface Data { widgets: BoardWidget[]; tokens: ApiToken[]; mcpUrl: string }

let confirmDelete: number | null = null;
let newToken: string | null = null;
let tokenErr: string | null = null;
let copied = "";
let ask = "";
let showSetup = false;
let watch: ReturnType<typeof setInterval> | null = null;
let watchUntil = 0;
let lastSig = "";

const IDEAS = [
  "Chart SCBA flow-test revenue by county for my reps, this year vs last",
  "Table of my top 15 customers this year with GP % and change vs last year",
  "Monthly loose goods revenue for each of my reps this year",
  "KPI of open quotes over $10K that are older than 30 days",
];

const sig = (w: BoardWidget[]) => w.map((x) => `${x.id}:${x.title}:${x.position}`).join("|");

/** While someone is building with Claude, check the board every 10s so new widgets pop in by themselves. */
function startWatch(app: App, d: Data) {
  lastSig = sig(d.widgets);
  if (watch) return;
  watch = setInterval(async () => {
    if (app.page !== "board") { clearInterval(watch!); watch = null; return; }
    if (document.hidden && Date.now() > watchUntil) return;
    try {
      const w = await api.board();
      if (sig(w) !== lastSig) {
        const added = w.length > lastSig.split("|").filter(Boolean).length;
        lastSig = sig(w);
        app.reload();
        if (added) app.toast("New widget from Claude added to your board");
      }
    } catch { /* offline; try again next tick */ }
  }, 10_000);
}

function claudeLink(request: string): string {
  const prompt = `Use my "Feld Fire Dashboard" connector.\n\n${request.trim()}\n\nGet every number from the dashboard tools (never estimate). Then save it to my board with add_board_widget so it shows on my dashboard, and tell me in one line what you added.`;
  return `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
}

function composer(app: App, d: Data) {
  const go = () => {
    if (!ask.trim()) return;
    window.open(claudeLink(ask), "_blank", "noopener");
    watchUntil = Date.now() + 15 * 60_000;
    if (!d.tokens.length) { showSetup = true; app.render(); }
  };
  return html`<section class="glass card" style="margin-bottom:16px">
    ${sectionHead("Ask Claude to build something", d.tokens.length ? "Describe what you want. Claude builds it with your data and it appears here on its own." : "Connect your Claude once (below), then describe what you want.")}
    <div class="composer">
      <textarea class="input" rows="2" placeholder="e.g. Show me which of my counties are down the most in service this year" .value=${ask}
        @input=${(e: Event) => { ask = (e.target as HTMLTextAreaElement).value; }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) go(); }}></textarea>
      <button class="btn primary" @click=${go}>${icons.sparkle}<span>Build with Claude</span></button>
    </div>
    <div class="chips">${IDEAS.map((t) => html`<button class="chip" @click=${() => { ask = t; app.render(); }}>${t}</button>`)}</div>
    <p class="muted small" style="margin:10px 0 0">Opens Claude in a new tab with your request filled in; press send there. Claude uses your own Claude account and only sees what you can see on this dashboard.</p>
  </section>`;
}

function setup(app: App, d: Data, createToken: () => Promise<void>, revoke: (id: number) => Promise<void>) {
  const url = newToken ? `${d.mcpUrl}/${newToken}` : null;
  const reachable = /^https:\/\//.test(d.mcpUrl) && !/your-tailnet/.test(d.mcpUrl);
  const open = showSetup || !d.tokens.length || !!newToken;
  return html`<section class="glass card mt">
    <div class="row" style="align-items:center">
      ${sectionHead("Connect your Claude", d.tokens.length ? `Connected · key ${d.tokens[0]!.last_used ? `last used ${timeAgo(d.tokens[0]!.last_used)}` : "not used yet"}` : "One time, about 2 minutes")}
      <span class="spacer"></span>
      ${d.tokens.length && !newToken ? html`<button class="link" @click=${() => { showSetup = !showSetup; app.render(); }}>${open ? "Hide" : "Show"} setup</button>` : nothing}
    </div>
    ${!reachable ? html`<div class="banner" style="margin:8px 0 12px">${icons.alert}<div><b>Not reachable from Claude yet.</b> Claude connects over the internet, so the admin needs to turn on Tailscale Funnel and set publicUrl in config/settings.json. Until then this won't work.</div></div>` : nothing}
    ${open ? html`<div class="grid g-2e">
      <ol class="steps">
        <li><b>Create your connector link</b> with the button. It only works for your login, so Claude sees exactly what you see here.</li>
        <li>Open <a class="link" href="https://claude.ai/customize/connectors" target="_blank" rel="noopener noreferrer">Claude's connector settings ↗</a> and choose <b>Add custom connector</b>. Name it <b>Feld Fire Dashboard</b>, paste the link, and click Add.</li>
        <li>Back here, type what you want above and click <b>Build with Claude</b>. In Claude, make sure the Feld Fire Dashboard connector is on, then send.</li>
      </ol>
      <div class="stack" style="gap:12px">
        ${url ? html`<div class="field"><label>Your connector link (shown once, copy it now)</label>
            <div class="row"><div class="code" style="flex:1;word-break:break-all">${url}</div><button class="btn primary" @click=${() => copy(app, url, "url")}>${copied === "url" ? "Copied" : "Copy"}</button></div>
            <div class="row"><button class="link" @click=${() => { newToken = null; showSetup = false; app.render(); }}>I added it in Claude, hide this</button></div></div>`
          : html`<div><button class="btn primary" @click=${createToken}>${icons.key}<span>${d.tokens.length ? "Create a new connector link" : "Create my connector link"}</span></button></div>`}
        ${tokenErr ? html`<div class="err-text">${tokenErr}</div>` : nothing}
        ${d.tokens.length ? html`<div class="panel-list">${d.tokens.map((t) => html`<div class="kv">
          <span>${t.label} · created ${fmtDate(t.created_at.slice(0, 10))} · ${t.last_used ? `used ${timeAgo(t.last_used)}` : "never used"}</span>
          <button class="btn ghost danger" @click=${() => revoke(t.id)}>Revoke</button></div>`)}</div>` : nothing}
      </div>
    </div>` : nothing}
  </section>`;
}

function copy(app: App, text: string, what: string) {
  const done = () => { copied = what; app.render(); setTimeout(() => { copied = ""; app.render(); }, 1800); };
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, () => undefined);
}

function widgetBody(w: BoardWidget) {
  if (w.error) return html`<div class="err-text">This widget can't run: ${w.error}</div>`;
  const r = w.result;
  if (!r) return nothing;
  if (w.spec.kind === "kpi") {
    const m = r.metrics[0]!;
    const v = r.totals[m.key] ?? null, p = r.priorTotals?.[m.key] ?? null;
    return html`<div class="kpi-value" style="font-size:36px">${formatMetric(v, m.format)}</div>
      ${p !== null && v !== null ? html`<div class="kpi-foot">${delta(v, p)}<span>vs ${formatMetric(p, m.format, true)} same period last year</span></div>` : nothing}`;
  }
  if (w.spec.kind === "chart" && r.dims.length === 1) return resultChart(r, w.spec.chart, 230);
  return resultTable(r, { maxHeight: 320 });
}

export const board: View<Data> = {
  title: () => "My Board",
  filters: [],
  async load() {
    const [widgets, t] = await Promise.all([api.board(), api.tokens()]);
    return { widgets, tokens: t.tokens, mcpUrl: t.mcpUrl };
  },
  render(app: App, d) {
    const move = async (id: number, dir: -1 | 1) => {
      const ids = d.widgets.map((w) => w.id);
      const i = ids.indexOf(id), j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j]!, ids[i]!];
      await api.reorder(ids).catch((e: Error) => app.toast(e.message));
      app.reload();
    };
    const remove = async (id: number) => {
      if (confirmDelete !== id) { confirmDelete = id; app.render(); return; }
      confirmDelete = null;
      await api.deleteWidget(id).catch((e: Error) => app.toast(e.message));
      app.reload();
    };
    const createToken = async () => {
      tokenErr = null;
      try { newToken = (await api.createToken("Claude")).token; } catch (e) { tokenErr = (e as Error).message; }
      app.reload();
    };
    const revoke = async (id: number) => { await api.revokeToken(id).catch((e: Error) => app.toast(e.message)); app.reload(); };

    startWatch(app, d);
    return html`
      ${composer(app, d)}
      ${d.widgets.length ? html`<div class="board">${d.widgets.map((w, i) => html`<section class="glass widget">
        <div class="card-head">
          <div style="min-width:0"><div class="card-title">${w.title}</div><div class="card-sub">${w.rangeLabel}${w.spec.note ? html` · ${w.spec.note}` : nothing}</div></div>
          <span class="spacer"></span>
          ${w.source === "claude" ? html`<span class="badge claude" title="Built with Claude">Claude</span>` : nothing}
          <button class="btn ghost icon" title="Move left" aria-label="Move left" ?disabled=${i === 0} @click=${() => move(w.id, -1)}>${icons.arrowLeft}</button>
          <button class="btn ghost icon" title="Move right" aria-label="Move right" ?disabled=${i === d.widgets.length - 1} @click=${() => move(w.id, 1)}>${icons.arrowRight}</button>
          ${w.result?.rows.length ? html`<button class="btn ghost icon" title="Download CSV" aria-label="Download CSV" @click=${() => downloadCsv(w.title, w.result!)}>${icons.download}</button>` : nothing}
          <button class="btn ghost ${confirmDelete === w.id ? "danger" : "icon"}" title="Remove" aria-label="Remove" @click=${() => remove(w.id)}>${confirmDelete === w.id ? "Remove?" : icons.trash}</button>
        </div>
        ${widgetBody(w)}
      </section>`)}</div>` : html`<section class="glass card empty-state">
        <div style="font-size:15px;color:var(--text-1);font-weight:600;margin-bottom:6px">Your board is empty</div>
        Pin anything from <a class="link" href=${app.href("explore")}>Explore</a>, or ask Claude above to build it for you.
      </section>`}

      ${setup(app, d, createToken, revoke)}`;
  },
};
